import { AlertCircle, Copy, X, Check } from 'lucide-react'
import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
  DialogDescription,
} from '@/components/ui/dialog'
import { DocumentStyle } from '../document/DocumentStyleSelector'

interface UpdateWithAIDialogProps {
  visible: boolean
  onHide: () => void
  onUpdateWithAI: () => void
  contentMarkdown: string
  documentStyle: DocumentStyle
  context: string
  linkedKnowledge: string
  onDuplicate: () => void
}

const UpdateWithAIDialog: React.FC<UpdateWithAIDialogProps> = ({
  visible,
  onHide,
  onUpdateWithAI,
  contentMarkdown,
  documentStyle,
  context,
  linkedKnowledge,
  onDuplicate,
}) => {
  const [isDuplicateButtonClicked, setIsDuplicateButtonClicked] =
    useState(false)

  const handleDuplicateClick = () => {
    onDuplicate()
    setIsDuplicateButtonClicked(true)
  }

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Document with AI</DialogTitle>
          <DialogDescription>Update whole document with AI</DialogDescription>
          <DialogClose onClick={onHide} className="absolute right-4 top-4">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="py-4">
          {/* Warning message */}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
              <strong className="text-amber-800">Warning:</strong>
              <span className="ml-1 text-amber-700">
                This will regenerate the entire document with AI.
              </span>
            </div>
            <p className="text-amber-700 mt-2 text-sm">
              This process may lead to unwanted changes in your document. We
              recommend making a duplicate of your document before proceeding.
            </p>
            <div className="mt-2">
              <Button
                onClick={handleDuplicateClick}
                variant="outline"
                size="sm"
                className="bg-amber-100 border-amber-300 text-amber-900"
                disabled={isDuplicateButtonClicked}
              >
                <Copy className="h-4 w-4 mr-2" />
                {isDuplicateButtonClicked
                  ? 'Node duplicated'
                  : 'Make a duplicate'}
              </Button>
            </div>
          </div>

          {/* Content to be updated */}
          <div className="mb-4">
            <h6 className="text-sm font-medium mb-2">Document Content</h6>
            <div className="bg-muted p-3 rounded-md max-h-[100px] overflow-auto text-sm">
              {contentMarkdown || (
                <em className="text-muted-foreground">No content</em>
              )}
            </div>
          </div>

          {/* Document Style */}
          <div className="mb-4"></div>
          <h6 className="text-sm font-medium mb-2">Document Style</h6>
          <div className="bg-muted p-3 rounded-md text-sm">
            {documentStyle.type === 'predefined' ? (
              <span>
                <strong>Predefined Style: </strong>
                {documentStyle.value}
              </span>
            ) : documentStyle.type === 'custom' ? (
              <span>
                <b>Custom Style: </b>
                {documentStyle.customValue}
              </span>
            ) : (
              <em className="text-muted-foreground">No style selected</em>
            )}
          </div>
        </div>

        {/* Context */}
        <div className="mb-4">
          <h6 className="text-sm font-medium mb-2">Context</h6>
          <div className="bg-muted p-3 rounded-md max-h-[100px] overflow-auto text-sm">
            {context || (
              <em className="text-muted-foreground">No context provided</em>
            )}
          </div>
        </div>

        {/* Linked Knowledge */}
        <div>
          <h6 className="text-sm font-medium mb-2">Linked Knowledge</h6>
          <div className="bg-muted p-3 rounded-md max-h-[100px] overflow-auto text-sm">
            {linkedKnowledge || (
              <em className="text-muted-foreground">No knowledge linked</em>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onHide} className="mr-2">
            Cancel
          </Button>
          <Button onClick={onUpdateWithAI}>
            <Check className="h-4 w-4 mr-2" />
            Proceed with Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateWithAIDialog
